import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { Plus, AlertTriangle, CalendarClock } from 'lucide-react';

import Sidebar from '../layouts/Sidebar';
import Badge from '../components/ui/Badge';
import Button from '../components/ui/Button';
import Modal from '../components/ui/Modal';
import DataTable from '../components/ui/DataTable';
import InventoryService from '../services/InventoryService';

const CATEGORIES = ['PPE', 'Medical', 'Detection', 'Decontamination', 'Other'];

const EMPTY_FORM = {
  item_name: '',
  category: 'PPE',
  quantity: 0,
  unit: 'unit',
  minQuantity: 0,
  location: '',
  expiryDate: '',
  notes: '',
};

export default function Inventory() {
  const [rows, setRows] = useState([]);
  const [alerts, setAlerts] = useState({ lowStock: [], expiring: [] });
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [deleteRow, setDeleteRow] = useState(null);

  const load = async () => {
    try {
      const [items, alertData] = await Promise.all([
        InventoryService.findAll(),
        InventoryService.alerts(),
      ]);
      setRows(items.data);
      setAlerts(alertData.data);
    } catch {
      toast.error('Failed to load inventory.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const openCreate = () => {
    setForm(EMPTY_FORM);
    setEditingId(null);
    setFormOpen(true);
  };

  const openEdit = (row) => {
    setForm({ ...EMPTY_FORM, ...row, expiryDate: row.expiryDate || '' });
    setEditingId(row.id);
    setFormOpen(true);
  };

  const handleSave = async () => {
    if (!form.item_name || !form.category) {
      toast.error('Item name and category are required.');
      return;
    }
    const payload = { ...form, expiryDate: form.expiryDate || null };
    try {
      if (editingId) {
        await InventoryService.update(editingId, payload);
        toast.success('Inventory item updated.');
      } else {
        await InventoryService.create(payload);
        toast.success('Inventory item added.');
      }
      setFormOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Save failed.');
    }
  };

  const handleDelete = async () => {
    try {
      await InventoryService.delete(deleteRow.id);
      toast.success('Inventory item deleted.');
      setDeleteRow(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Delete failed.');
    }
  };

  const headCells = [
    {
      id: 'item_name',
      label: 'Item',
      render: (row) => (
        <div>
          <div className="font-bold text-neutral-800 dark:text-neutral-100">{row.item_name}</div>
          {row.notes && <div className="text-xs text-neutral-500 mt-0.5 line-clamp-1 dark:text-neutral-500">{row.notes}</div>}
        </div>
      ),
    },
    { id: 'category', label: 'Category', render: (row) => <Badge>{row.category}</Badge> },
    {
      id: 'quantity',
      label: 'Stock',
      render: (row) => (
        <span className="text-neutral-700 font-medium dark:text-neutral-300">
          {row.quantity} <span className="text-xs text-neutral-400 dark:text-neutral-500">{row.unit}</span>
        </span>
      ),
    },
    { id: 'minQuantity', label: 'Min Level' },
    { id: 'location', label: 'Location' },
    {
      id: 'expiryDate',
      label: 'Expiry',
      render: (row) => <span className="text-neutral-600 dark:text-neutral-300">{row.expiryDate || '—'}</span>,
    },
    {
      id: 'lowStock',
      label: 'Status',
      render: (row) =>
        row.lowStock
          ? <Badge variant="red">Low Stock</Badge>
          : <Badge variant="green">OK</Badge>,
    },
  ];

  const setField = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  return (
    <Sidebar title="Inventory">
      <section className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h3 className="text-lg font-bold text-neutral-800 dark:text-neutral-100">Resource Inventory &amp; Logistics</h3>
            <p className="text-xs text-neutral-500 mt-1 dark:text-neutral-500">CBRNE response stock, PPE, detection and decon resources.</p>
          </div>
          <Button icon={Plus} onClick={openCreate}>Add Item</Button>
        </div>

        {(alerts.lowStock.length > 0 || alerts.expiring.length > 0) && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {alerts.lowStock.length > 0 && (
              <div className="flex items-start gap-3 rounded-xl border border-red-100 bg-red-50 px-4 py-3 dark:bg-red-500/10 dark:border-red-500/20">
                <AlertTriangle className="w-4 h-4 text-red-600 mt-0.5 shrink-0 dark:text-red-400" />
                <div className="text-sm text-red-700 dark:text-red-400">
                  <b>{alerts.lowStock.length} item(s) at or below minimum stock:</b>{' '}
                  {alerts.lowStock.map((i) => i.item_name).join(', ')}
                </div>
              </div>
            )}
            {alerts.expiring.length > 0 && (
              <div className="flex items-start gap-3 rounded-xl border border-amber-100 bg-amber-50 px-4 py-3 dark:bg-amber-500/10 dark:border-amber-500/20">
                <CalendarClock className="w-4 h-4 text-amber-600 mt-0.5 shrink-0 dark:text-amber-400" />
                <div className="text-sm text-amber-700 dark:text-amber-400">
                  <b>{alerts.expiring.length} item(s) expiring within 90 days:</b>{' '}
                  {alerts.expiring.map((i) => i.item_name).join(', ')}
                </div>
              </div>
            )}
          </div>
        )}

        <DataTable
          headCells={headCells}
          rows={rows}
          onEdit={openEdit}
          onDelete={(row) => setDeleteRow(row)}
          loading={loading}
          emptyLabel={loading ? 'Loading inventory…' : 'No inventory items yet.'}
        />
      </section>

      <Modal
        open={formOpen}
        onClose={() => setFormOpen(false)}
        title={editingId ? 'Edit Inventory Item' : 'Add Inventory Item'}
        subtitle="Track stock levels, storage location and expiry."
        confirmLabel={editingId ? 'Save Changes' : 'Add Item'}
        onConfirm={handleSave}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div>
            <label className="form-label" htmlFor="inv-name">Item Name *</label>
            <input id="inv-name" className="form-input" value={form.item_name} onChange={setField('item_name')} />
          </div>
          <div>
            <label className="form-label" htmlFor="inv-category">Category *</label>
            <select id="inv-category" className="form-input" value={form.category} onChange={setField('category')}>
              {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label className="form-label" htmlFor="inv-quantity">Quantity</label>
            <input id="inv-quantity" type="number" min="0" className="form-input" value={form.quantity} onChange={setField('quantity')} />
          </div>
          <div>
            <label className="form-label" htmlFor="inv-unit">Unit</label>
            <input id="inv-unit" className="form-input" value={form.unit || ''} onChange={setField('unit')} />
          </div>
          <div>
            <label className="form-label" htmlFor="inv-min">Minimum Level (reorder alert)</label>
            <input id="inv-min" type="number" min="0" className="form-input" value={form.minQuantity} onChange={setField('minQuantity')} />
          </div>
          <div>
            <label className="form-label" htmlFor="inv-location">Storage Location</label>
            <input id="inv-location" className="form-input" value={form.location || ''} onChange={setField('location')} />
          </div>
          <div>
            <label className="form-label" htmlFor="inv-expiry">Expiry Date</label>
            <input id="inv-expiry" type="date" className="form-input" value={form.expiryDate || ''} onChange={setField('expiryDate')} />
          </div>
          <div className="md:col-span-2">
            <label className="form-label" htmlFor="inv-notes">Notes</label>
            <textarea id="inv-notes" className="form-input" rows={3} value={form.notes || ''} onChange={setField('notes')} />
          </div>
        </div>
      </Modal>

      <Modal
        open={Boolean(deleteRow)}
        onClose={() => setDeleteRow(null)}
        title="Delete Inventory Item"
        subtitle={deleteRow ? deleteRow.item_name : ''}
        confirmLabel="Delete"
        onConfirm={handleDelete}
      >
        <p className="text-sm text-neutral-600 dark:text-neutral-300">
          This removes the item from inventory tracking. This action cannot be undone.
        </p>
      </Modal>
    </Sidebar>
  );
}
